import type { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Select from '@atlaskit/select';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import EmptyState from '@atlaskit/empty-state';
import Spinner from '@atlaskit/spinner';
import Link from '@atlaskit/link';
import SearchFailedImage from '#assets/search-failed.svg';

import { useMcpContext } from '../McpContextProvider';
import TrelloWorkspaceMultiSelect from '../../MCP/TrelloWorkspaceSelector/TrelloWorkspaceMultiSelect';
import { trelloWorkspaceSelectorMessages } from '../../MCP/TrelloWorkspaceSelector/messages';
import type { TrelloWorkspace } from '../../MCP/domain/trelloTypes';
import { useSwitchAccountLink } from '../../../../definitions/user';

const stackStyle = xcss({ alignSelf: 'stretch' });

const selectStyle = xcss({ width: '100%', maxWidth: '350px' });

type WorkspaceOption = {
  label: string;
  value: string;
  workspace: TrelloWorkspace;
};

const TRELLO_SELECT_INPUT_ID = 'oauth-consent-mcp-trello-workspace-select';

/**
 * Trello workspace selector for the MCP consent screen.
 *
 * When `showHeading` is true (Trello-only flow) the component renders
 * its own "Authorize access for:" heading.  When false (mixed flow) the
 * component assumes it lives under the shared SiteSelector heading and
 * shows a short explanation instead.
 */
const TrelloWorkspaceSelector: FunctionComponent<{
  /** Whether to render the "Authorize access for:" heading (Trello-only flow). */
  showHeading?: boolean;
}> = ({ showHeading = false }) => {
  const {
    availableTrelloWorkspaces,
    selectedTrelloWorkspaces,
    setSelectedTrelloWorkspaces,
    isTrelloWorkspaceMultiSelectEnabled,
    isTrelloMultiWorkspaceSelectorEnabled,
    maxSelectableTrelloWorkspaces,
    clientName,
    scopeClassification,
    trelloWorkspacesLoading,
    hasTrelloInboxPlannerScopes,
  } = useMcpContext();
  const { formatMessage } = useIntl();

  const isMixed =
    scopeClassification.hasAtlassian && scopeClassification.hasTrello;
  const switchAccountUrl = useSwitchAccountLink();

  if (trelloWorkspacesLoading) {
    return (
      <Stack space="space.100" alignInline="center">
        {showHeading && (
          <Box as="label" htmlFor={TRELLO_SELECT_INPUT_ID}>
            <Heading size="small">
              {formatMessage(trelloWorkspaceSelectorMessages.siteLabel)}
            </Heading>
          </Box>
        )}
        <Spinner size="medium" />
      </Stack>
    );
  }

  if (availableTrelloWorkspaces.length === 0) {
    const switchAccountLink = (
      <Link href={switchAccountUrl}>
        <FormattedMessage
          {...trelloWorkspaceSelectorMessages.noWorkspacesSwitchAccount}
        />
      </Link>
    );
    const contactSupportLink = (
      <Link
        href="https://support.atlassian.com/trello"
        target="_blank"
        rel="noopener noreferrer"
      >
        <FormattedMessage
          {...trelloWorkspaceSelectorMessages.noWorkspacesContactSupport}
        />
      </Link>
    );

    // In the mixed flow, show a compact inline message so that the
    // Atlassian site permissions remain visible and prominent.
    if (isMixed) {
      return (
        <Text as="p" size="small" color="color.text.subtle">
          <FormattedMessage
            {...trelloWorkspaceSelectorMessages.noWorkspacesCompact}
            values={{ switchAccountLink, contactSupportLink }}
          />
        </Text>
      );
    }

    // Inbox and Planner are account level, so the flow stays usable without a
    // workspace. Show a disabled selector rather than a dead-end empty state.
    if (hasTrelloInboxPlannerScopes) {
      return (
        <Stack space="space.100" xcss={stackStyle}>
          {showHeading && (
            <Box as="label" htmlFor={TRELLO_SELECT_INPUT_ID}>
              <Heading size="small">
                {formatMessage(
                  trelloWorkspaceSelectorMessages.multiSelectLabel,
                )}
              </Heading>
            </Box>
          )}
          <Box xcss={selectStyle}>
            <Select
              inputId={TRELLO_SELECT_INPUT_ID}
              options={[]}
              value={null}
              isDisabled
              placeholder={formatMessage(
                trelloWorkspaceSelectorMessages.noWorkspaceAvailablePlaceholder,
              )}
            />
          </Box>
        </Stack>
      );
    }

    // Trello-only flow: show a full empty state
    return (
      <EmptyState
        header={formatMessage(
          trelloWorkspaceSelectorMessages.noWorkspacesHeading,
        )}
        description={
          <FormattedMessage
            {...trelloWorkspaceSelectorMessages.noWorkspacesDescription}
            values={{ switchAccountLink, contactSupportLink }}
          />
        }
        imageUrl={SearchFailedImage}
      />
    );
  }

  if (isTrelloMultiWorkspaceSelectorEnabled) {
    return (
      <Stack space="space.100" xcss={stackStyle}>
        <TrelloWorkspaceMultiSelect
          availableWorkspaces={availableTrelloWorkspaces}
          selectedWorkspaces={selectedTrelloWorkspaces}
          setSelectedWorkspaces={setSelectedTrelloWorkspaces}
          showHeading={showHeading}
          maxSelectableWorkspaces={maxSelectableTrelloWorkspaces}
          clientName={clientName}
        />
      </Stack>
    );
  }

  const options: WorkspaceOption[] = availableTrelloWorkspaces.map((ws) => ({
    label: ws.displayName,
    value: ws.id,
    workspace: ws,
  }));

  const firstSelectedWorkspace = selectedTrelloWorkspaces[0] ?? null;
  const selectedOption = firstSelectedWorkspace
    ? (options.find((o) => o.value === firstSelectedWorkspace.id) ?? null)
    : null;
  const selectedOptions = (selectedTrelloWorkspaces ?? [])
    .map((workspace) => options.find((o) => o.value === workspace.id))
    .filter((option): option is WorkspaceOption => option != null);

  return (
    <Stack space="space.100" xcss={stackStyle}>
      {showHeading && (
        <Box as="label" htmlFor={TRELLO_SELECT_INPUT_ID}>
          <Heading size="small">
            {formatMessage(trelloWorkspaceSelectorMessages.siteLabel)}
          </Heading>
        </Box>
      )}
      <Box xcss={selectStyle}>
        <Select<WorkspaceOption, boolean>
          inputId={TRELLO_SELECT_INPUT_ID}
          isMulti={isTrelloWorkspaceMultiSelectEnabled}
          options={options}
          value={
            isTrelloWorkspaceMultiSelectEnabled
              ? selectedOptions
              : selectedOption
          }
          onChange={(option) => {
            if (isTrelloWorkspaceMultiSelectEnabled) {
              const selectedWorkspaceOptions = Array.isArray(option)
                ? (option as WorkspaceOption[])
                : [];
              setSelectedTrelloWorkspaces(
                selectedWorkspaceOptions.map((selected) => selected.workspace),
              );
              return;
            }
            const selectedWorkspace =
              (option as WorkspaceOption | null)?.workspace ?? null;
            setSelectedTrelloWorkspaces(
              selectedWorkspace ? [selectedWorkspace] : [],
            );
          }}
          placeholder={formatMessage(
            trelloWorkspaceSelectorMessages.placeholder,
          )}
          isClearable
        />
      </Box>
      {isMixed && (
        <Text as="p" size="small" color="color.text.subtle">
          {formatMessage(trelloWorkspaceSelectorMessages.mixedDescription)}
        </Text>
      )}
    </Stack>
  );
};

export default TrelloWorkspaceSelector;
